import { cn } from "@newxsystems/shared";
import type { AnchorHTMLAttributes } from "react";
import { getComponent } from "../../../../client";
import { BlockActions } from "../../../shared/components/BlockActions/BlockActions";
import { BlockCommonContent } from "../../../shared/components/BlockCommonContent/BlockCommonContent";
import { BlockContainer } from "../../../shared/components/BlockContainer/BlockContainer";
import { BlockImage } from "../../../shared/components/BlockImage/BlockImage";
import { BlockRichText } from "../../../shared/components/BlockRichText/BlockRichText";
import type { IFeaturedTestimonialStandardProps } from "./FeaturedTestimonialStandard.props";

export const FeaturedTestimonialStandard = ({
  alignment,
  anchorId,
  buttons,
  description,
  eyebrow,
  heading,
  parentClass,
  spacing,
  testimonial,
}: IFeaturedTestimonialStandardProps) => {
  const Link = getComponent<AnchorHTMLAttributes<HTMLAnchorElement>>("Link");
  const hasIntro = Boolean(eyebrow || heading || description);
  const hasActions = Boolean(buttons?.length);

  return (
    <BlockContainer anchorId={anchorId} parentClass={parentClass} spacing={spacing}>
      <div className="flex flex-col gap-12 md:gap-16">
        {hasIntro || hasActions ? (
          <div className="flex flex-col gap-8">
            {hasIntro ? (
              <BlockCommonContent
                alignment={alignment}
                description={description}
                eyebrow={eyebrow}
                heading={heading}
                headingLevel="h2"
              />
            ) : null}
            {buttons?.length ? <BlockActions actions={buttons} alignment={alignment} /> : null}
          </div>
        ) : null}

        {testimonial ? (
          <div
            className={cn(
              "grid min-w-0 grid-cols-1 gap-8 md:items-start md:gap-12",
              testimonial.portrait && "md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]",
            )}
          >
            {testimonial.portrait ? (
              <BlockImage
                className="w-full"
                effectiveAspectRatio="1:1"
                image={testimonial.portrait}
                imageClassName="object-cover"
              />
            ) : null}

            <div className="min-w-0 text-start">
              <blockquote className="text-[34px]/[1] font-semibold tracking-[-0.025em] text-pretty md:text-[44px] [&_p+p]:mt-4">
                <BlockRichText content={testimonial.quote} />
              </blockquote>
              <div className="mt-8 flex flex-col gap-1">
                <p className="text-[17px]/[1.3] font-semibold [overflow-wrap:anywhere]">
                  {testimonial.name}
                </p>
                {testimonial.attribution ? (
                  <p className="font-mono text-[12px]/[1.45] text-onSurface-500 [overflow-wrap:anywhere]">
                    {testimonial.attribution}
                  </p>
                ) : null}
              </div>
              {testimonial.link && Link ? (
                <Link
                  aria-label={testimonial.link.accessibilityLabel ?? testimonial.link.label}
                  className="mt-8 inline-flex min-h-10 items-center font-mono text-[12px]/[1.45] text-electric-blue underline decoration-1 underline-offset-4 transition-colors duration-200 hover:text-paprika focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-ring"
                  href={testimonial.link.href}
                  rel={testimonial.link.openInNewTab === true ? "noopener noreferrer" : undefined}
                  target={testimonial.link.openInNewTab === true ? "_blank" : undefined}
                >
                  {testimonial.link.label}
                </Link>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </BlockContainer>
  );
};

FeaturedTestimonialStandard.displayName = "FeaturedTestimonial::FeaturedTestimonialStandard";
